import { useEffect } from "react";
import { APP_STORE_URL, PLAY_URL, storeHref, type WebPlatform } from "./appPromo";
import "./AppWall.css";

/**
 * The phone-browser wall: on a phone, the reader is not served in the browser at all. The
 * wall replaces the entire shell (App.tsx early-returns into it), pitches the app once, and
 * offers the stores. It deliberately has no dismiss and no preview of the day's content:
 * the product decision is that a phone reads InSnip in the app, where the deck, the gestures
 * and the offline round actually work.
 *
 * Visually it is the app's own poster: the noir ground, the gold ambient, the deck rendered
 * as three fanned card slivers (pure CSS, no fabricated headlines), and one platform-aware
 * call to action above the two official store badges. Single deliberate look, always noir,
 * independent of the web theme: this screen IS the app's trailer, not a page of the site.
 */
export default function AppWall({
  platform,
  onStoreClick,
  onSeen,
}: {
  platform: WebPlatform;
  onStoreClick: (surface: string) => void;
  onSeen: () => void;
}) {
  useEffect(() => {
    onSeen();
    // The wall owns the whole viewport; nothing behind it should scroll or bounce.
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryHref = storeHref(platform);
  const primaryLabel = platform === "ios" ? "Get InSnip on the App Store" : "Get InSnip on Google Play";

  return (
    <div className="app-wall" role="main" aria-label="Get the InSnip app">
      <div className="aw-glow" aria-hidden="true" />
      <header className="aw-brand">
        <span className="aw-wordmark">
          In<i>Snip</i>
        </span>
        <span className="aw-motto">Pure signal, no noise.</span>
      </header>

      {/* The deck, as an object: three slivers fanned like the cards they are. No fake
          headlines, just the material (poster band, type bars) reading as "a deck of stories". */}
      <div className="aw-deck" aria-hidden="true">
        <div className="aw-card aw-card--back" />
        <div className="aw-card aw-card--mid" />
        <div className="aw-card aw-card--front">
          <div className="aw-card-poster" />
          <div className="aw-card-lines">
            <i style={{ width: "82%" }} />
            <i style={{ width: "64%" }} />
            <i className="aw-line-dim" style={{ width: "74%" }} />
            <i className="aw-line-dim" style={{ width: "58%" }} />
          </div>
        </div>
      </div>

      <h1 className="aw-headline">The reader lives in the app</h1>
      <p className="aw-pitch">
        Today's ten stories, source-checked, about a minute each. Built for your thumb, with the
        swipe, the streak and the round that a browser tab cannot give you.
      </p>

      <a
        className="aw-cta"
        href={primaryHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onStoreClick(platform === "ios" ? "wall_ios_primary" : "wall_android_primary")}
      >
        {primaryLabel}
      </a>
      <span className="aw-fine">Free. No ads in your feed. Installed in under a minute.</span>

      <div className="aw-badges">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
          onClick={() => onStoreClick("wall_ios_badge")}
        >
          <img src="/app-store-badge.svg" alt="Download on the App Store" width={135} height={40} />
        </a>
        <a
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          onClick={() => onStoreClick("wall_android_badge")}
        >
          <img src="/google-play-badge.png" alt="Get it on Google Play" width={135} height={40} />
        </a>
      </div>

      <ul className="aw-proof" aria-label="What you get">
        <li>Every story source-checked</li>
        <li>A finishable daily round</li>
        <li>No doomscroll by design</li>
      </ul>

      <footer className="aw-foot">
        On a computer? <strong>insnip.com</strong> opens the full reader.
      </footer>
    </div>
  );
}
